/**
 * Фронтенд-логика блока «Оглавление».
 */
(function () {
	const BLOCK_SELECTOR = '.wp-block-wps-blocks-toc';
	const LIST_SELECTOR = '.wp-block-wps-blocks-toc__list';
	const LINK_CLASS = 'wp-block-wps-blocks-toc__link';
	const ITEM_CLASS = 'wp-block-wps-blocks-toc__item';
	const CURRENT_CLASS = 'current';

	function normalizeText(value) {
		return String(value || '')
			.trim()
			.replace(/\s+/g, ' ');
	}

	function makeSlug(text) {
		const fallback = 'section';
		const normalized = normalizeText(text)
			.toLowerCase()
			.normalize('NFKD')
			.replace(/[\u0300-\u036f]/g, '')
			.replace(/[^a-z0-9а-яё\s-]/gi, '')
			.replace(/[\s_-]+/g, '-')
			.replace(/^-+|-+$/g, '');
		return normalized || fallback;
	}

	function getPostContentRoot(tocBlock) {
		const article = tocBlock.closest('article');
		if (article) {
			const articleContent = article.querySelector('.entry-content, .post-content, .wp-block-post-content');
			if (articleContent) {
				return articleContent;
			}
			return article;
		}

		return document.querySelector('.entry-content, .post-content, .wp-block-post-content');
	}

	function ensureElementId(element, usedIds) {
		const currentId = String(element.id || '').trim();
		if (currentId && !usedIds.has(currentId)) {
			usedIds.add(currentId);
			return currentId;
		}

		const baseSlug = makeSlug(element.textContent);
		let nextId = baseSlug;
		let index = 2;
		while (!nextId || usedIds.has(nextId) || (document.getElementById(nextId) && document.getElementById(nextId) !== element)) {
			nextId = `${baseSlug}-${index}`;
			index += 1;
		}

		element.id = nextId;
		usedIds.add(nextId);
		return nextId;
	}

	function isInsideToc(element) {
		return !!element.closest(BLOCK_SELECTOR);
	}

	function getTargets(tocBlock, sourceType, sourceClass) {
		const root = getPostContentRoot(tocBlock);
		if (!root) {
			return [];
		}

		let selector = 'h2';
		if (sourceType === 'class') {
			if (!sourceClass) {
				return [];
			}
			selector = `.${sourceClass}`;
		}

		const nodes = root.querySelectorAll(selector);
		return Array.from(nodes).filter((node) => {
			if (!(node instanceof HTMLElement)) {
				return false;
			}
			if (isInsideToc(node)) {
				return false;
			}
			return normalizeText(node.textContent).length > 0;
		});
	}

	function clearCurrentState(linksMap) {
		linksMap.forEach((item) => {
			item.link.classList.remove(CURRENT_CLASS);
			item.item.classList.remove(CURRENT_CLASS);
		});
	}

	function setCurrent(id, linksMap) {
		clearCurrentState(linksMap);
		const current = linksMap.get(id);
		if (!current) {
			return;
		}
		current.link.classList.add(CURRENT_CLASS);
		current.item.classList.add(CURRENT_CLASS);
	}

	function updateHash(id) {
		if (!id) {
			return;
		}
		if (window.history && typeof window.history.replaceState === 'function') {
			window.history.replaceState(null, '', `#${id}`);
			return;
		}
		window.location.hash = id;
	}

	function scrollToTarget(id, linksMap, immediate) {
		const entry = linksMap.get(id);
		if (!entry) {
			return;
		}

		// Lenis резолвит строку через querySelector. id с цифры (`4-покупка-…`) — невалидный селектор, scrollTo кидает исключение и скролл не происходит.
		if (window.asterLenis && typeof window.asterLenis.scrollTo === 'function') {
			window.asterLenis.scrollTo(entry.target, {
				immediate: immediate === true,
			});
			return;
		}

		const top = entry.target.getBoundingClientRect().top + window.scrollY;
		window.scrollTo({
			top: Math.max(0, top),
			behavior: immediate ? 'auto' : 'smooth',
		});
	}

	function buildToc(tocBlock) {
		const list = tocBlock.querySelector(LIST_SELECTOR);
		if (!list) {
			return;
		}

		const sourceType = tocBlock.dataset.sourceType === 'class' ? 'class' : 'h2';
		const sourceClass = String(tocBlock.dataset.sourceClass || '').trim().replace(/^\./, '');
		const targets = getTargets(tocBlock, sourceType, sourceClass);

		list.innerHTML = '';
		if (!targets.length) {
			tocBlock.remove();
			return;
		}

		tocBlock.removeAttribute('hidden');

		const usedIds = new Set();
		const linksMap = new Map();

		targets.forEach((target) => {
			const headingText = normalizeText(target.textContent);
			const targetId = ensureElementId(target, usedIds);
			const item = document.createElement('li');
			const link = document.createElement('a');

			item.className = ITEM_CLASS;
			link.className = LINK_CLASS;
			link.href = `#${targetId}`;
			link.textContent = headingText;
			link.dataset.targetId = targetId;

			item.appendChild(link);
			list.appendChild(item);
			linksMap.set(targetId, { link, item, target });
		});

		const updateCurrentByScroll = () => {
			let currentId = '';
			const topOffset = 120;

			linksMap.forEach((entry, id) => {
				const rect = entry.target.getBoundingClientRect();
				if (rect.top <= topOffset) {
					currentId = id;
				}
			});

			if (!currentId) {
				const first = linksMap.values().next();
				if (!first.done) {
					currentId = first.value.link.dataset.targetId;
				}
			}

			if (currentId) {
				setCurrent(currentId, linksMap);
			}
		};

		if ('IntersectionObserver' in window) {
			const observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (!entry.isIntersecting) {
							return;
						}
						const id = entry.target.id;
						if (id) {
							setCurrent(id, linksMap);
						}
					});
				},
				{
					root: null,
					rootMargin: '0px 0px -70% 0px',
					threshold: 0.01,
				}
			);

			linksMap.forEach((entry) => observer.observe(entry.target));
		} else {
			window.addEventListener('scroll', updateCurrentByScroll, { passive: true });
		}

		list.addEventListener('click', (event) => {
			const link = event.target.closest(`.${LINK_CLASS}`);
			if (!link) {
				return;
			}

			const id = link.dataset.targetId;
			if (!id) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
			setCurrent(id, linksMap);
			updateHash(id);
			scrollToTarget(id, linksMap);
		});

		const rawHash = String(window.location.hash || '').replace(/^#/, '');
		let hash = rawHash;
		try {
			hash = decodeURIComponent(rawHash);
		} catch (error) {
			hash = rawHash;
		}

		if (hash && linksMap.has(hash)) {
			setCurrent(hash, linksMap);
			const scrollToHash = () => scrollToTarget(hash, linksMap, true);
			// id заголовков появляются только здесь, нативный переход по хешу их уже не находит.
			window.setTimeout(scrollToHash, 0);
			if (document.readyState !== 'complete') {
				window.addEventListener('load', scrollToHash, { once: true });
			}
			return;
		}

		updateCurrentByScroll();
	}

	function init() {
		const blocks = document.querySelectorAll(BLOCK_SELECTOR);
		blocks.forEach((tocBlock) => {
			if (!(tocBlock instanceof HTMLElement)) {
				return;
			}
			buildToc(tocBlock);
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
